import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  parseISO,
  startOfMonth,
  subMonths,
} from 'date-fns';
import React, { useState } from 'react';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import api from '../../../api/client';
import ReportHelpButton from '../../../components/genericos/ReportHelpButton';
import { CURSOS } from '../../../constants/enums';
import { useCentros } from '../../../hooks/useCentros';
import { useFormatos } from '../../../hooks/useFormatos';
import { useProyectos } from '../../../hooks/useProyectos';

type KpiSeleccionado =
  | 'altas'
  | 'bajas'
  | 'saldo'
  | 'altaAcademia'
  | 'bajaAcademiaNeta'
  | 'altaProyecto'
  | 'bajaProyectoNeta'
  | 'bajaSoloProyecto'
  | 'transferenciasSalientes'
  | 'reactivacionesProyecto'
  | null;

type KpiDetalleRow = {
  id: number;
  nombre: string;
  email: string;
  fecha?: string;
  detalle?: string;
};

interface MotivosBaja {
  [key: string]: number;
}

interface ClienteBaja {
  id: number;
  nombre: string;
  email: string;
  fechaBaja: string;
  actorNombre: string;
  motivo?: string;
  mes?: string;
}

interface ClienteAlta {
  id: number;
  nombre: string;
  email: string;
  fechaAlta: string;
  mes?: string;
}

interface MesData {
  mes: string;
  altas: number;
  bajas: number;
  motivosBaja: MotivosBaja;
}

interface ReporteResponse {
  meses: MesData[];
  clientesPorMotivo: Record<string, ClienteBaja[]>;
  clientesAltasPorMes: Record<string, ClienteAlta[]>;
  clientesBajasPorMes: Record<string, ClienteBaja[]>;
  meta?: {
    definicionAlta?: string;
    definicionBaja?: string;
    includeAutoBajas?: boolean;
    universoAlumnos?: number;
    churnWindowDays?: number;
    kpis?: {
      academia?: {
        altasPeriodo: number;
        bajasNetasPeriodo: number;
        detalles?: {
          altasPeriodo?: KpiDetalleRow[];
          bajasNetasPeriodo?: KpiDetalleRow[];
        };
      };
      proyecto?: {
        activa: boolean;
        altasPeriodo: number;
        bajasNetasPeriodo: number;
        bajasSoloProyecto: number;
        transferenciasSalientes: number;
        reactivacionesProyecto: number;
        proyectoIds?: number[];
        detalles?: {
          altasPeriodo?: KpiDetalleRow[];
          bajasNetasPeriodo?: KpiDetalleRow[];
          bajasSoloProyecto?: KpiDetalleRow[];
          transferenciasSalientes?: KpiDetalleRow[];
          reactivacionesProyecto?: KpiDetalleRow[];
        };
      };
    };
  };
}

const COLORS = {
  BAJA: '#ef5350',
  NO_MAS_CLASE: '#ff9800',
  ERROR_CREACION: '#42a5f5',
};

const MOTIVO_LABELS = {
  BAJA: 'Baja voluntaria',
  NO_MAS_CLASE: 'Sin clases futuras',
  ERROR_CREACION: 'Error en creación',
};

export default function AltasBajasReport() {
  const { rows: proyectos = [] } = useProyectos({ activos: true });
  const { rows: centros = [] } = useCentros();
  const { rows: formatos = [] } = useFormatos();

  const [desde, setDesde] = useState<string>(
    format(subMonths(startOfMonth(new Date()), 6), 'yyyy-MM-dd'),
  );
  const [hasta, setHasta] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [proyectoIds, setProyectoIds] = useState<number[]>([]);
  const [grades, setGrades] = useState<string[]>([]);
  const [centroIds, setCentroIds] = useState<number[]>([]);
  const [formatoIds, setFormatoIds] = useState<number[]>([]);
  const [includeAutoBajas, setIncludeAutoBajas] = useState<boolean>(true);
  const [motivoSeleccionado, setMotivoSeleccionado] = useState<string | null>(null);
  const [kpiSeleccionado, setKpiSeleccionado] = useState<KpiSeleccionado>(null);

  const { data, isLoading, refetch } = useQuery<ReporteResponse>({
    queryKey: [
      'reporteAltasBajas',
      desde,
      hasta,
      proyectoIds.join(','),
      grades.join(','),
      centroIds.join(','),
      formatoIds.join(','),
      includeAutoBajas,
    ],
    queryFn: async () => {
      const params: Record<string, string | undefined> = {
        desde,
        hasta,
        proyectoIds: proyectoIds.length ? proyectoIds.join(',') : undefined,
        grades: grades.length ? grades.join(',') : undefined,
        centroIds: centroIds.length ? centroIds.join(',') : undefined,
        formatoIds: formatoIds.length ? formatoIds.join(',') : undefined,
        includeAutoBajas: includeAutoBajas ? 'true' : 'false',
      };

      return api.get<ReporteResponse>('/clientes/reportes/altas-bajas', {
        params,
      });
    },
    enabled: false, // Solo ejecutar cuando el usuario haga clic en "Generar"
  });

  // Preparar datos para el gráfico de líneas
  const mesesMap = new Map<string, MesData>();
  data?.meses.forEach((m) => mesesMap.set(m.mes, m));

  const clientesAltas = data ? Object.values(data.clientesAltasPorMes || {}).flat() : [];
  const clientesBajas = data ? Object.values(data.clientesBajasPorMes || {}).flat() : [];

  const desdeDate = parseISO(desde);
  const hastaDate = parseISO(hasta);
  const rangoDias = differenceInCalendarDays(hastaDate, desdeDate) + 1;
  const usarDiario = rangoDias <= 45;

  const lineChartData = usarDiario
    ? (() => {
        const altasPorDia = new Map<string, number>();
        const bajasPorDia = new Map<string, number>();

        clientesAltas.forEach((c) => {
          const key = format(new Date(c.fechaAlta), 'yyyy-MM-dd');
          altasPorDia.set(key, (altasPorDia.get(key) || 0) + 1);
        });
        clientesBajas.forEach((c) => {
          const key = format(new Date(c.fechaBaja), 'yyyy-MM-dd');
          bajasPorDia.set(key, (bajasPorDia.get(key) || 0) + 1);
        });

        const dias = eachDayOfInterval({ start: desdeDate, end: hastaDate });
        return dias.map((d) => {
          const key = format(d, 'yyyy-MM-dd');
          const altas = altasPorDia.get(key) || 0;
          const bajas = bajasPorDia.get(key) || 0;
          return {
            mes: key,
            Altas: altas,
            Bajas: bajas,
            Saldo: altas - bajas,
          };
        });
      })()
    : (() => {
        const desdeMes = startOfMonth(desdeDate);
        const hastaMes = startOfMonth(hastaDate);
        const mesesRango: string[] = [];
        for (let cursor = desdeMes; cursor <= hastaMes; cursor = addMonths(cursor, 1)) {
          mesesRango.push(format(cursor, 'yyyy-MM'));
        }

        return mesesRango.map((mes) => {
          const m = mesesMap.get(mes);
          const altas = m?.altas ?? 0;
          const bajas = m?.bajas ?? 0;
          return {
            mes,
            Altas: altas,
            Bajas: bajas,
            Saldo: altas - bajas,
          };
        });
      })();

  // Preparar datos para el gráfico de pastel (motivos de baja)
  const motivosData = data?.meses.reduce(
    (acc, mes) => {
      Object.entries(mes.motivosBaja).forEach(([motivo, count]) => {
        acc[motivo] = (acc[motivo] || 0) + count;
      });
      return acc;
    },
    {} as Record<string, number>,
  );

  const pieChartData = motivosData
    ? Object.entries(motivosData).map(([motivo, value]) => ({
        name: MOTIVO_LABELS[motivo as keyof typeof MOTIVO_LABELS] || motivo,
        value,
        motivo,
      }))
    : [];

  // Obtener clientes del motivo seleccionado
  const clientesSeleccionados =
    motivoSeleccionado && data?.clientesPorMotivo
      ? data.clientesPorMotivo[motivoSeleccionado] || []
      : [];

  // Handler para clic en el gráfico
  const handlePieClick = (entry: { motivo: string }) => {
    setMotivoSeleccionado(entry.motivo);
  };

  // Calcular totales
  const totales = data?.meses.reduce(
    (acc, mes) => ({
      altas: acc.altas + mes.altas,
      bajas: acc.bajas + mes.bajas,
    }),
    { altas: 0, bajas: 0 },
  );

  const getDetalleKpi = (): { titulo: string; rows: KpiDetalleRow[] } => {
    if (!data || !kpiSeleccionado) return { titulo: '', rows: [] };

    if (kpiSeleccionado === 'altas') {
      return {
        titulo: `Clientes de alta (${clientesAltas.length})`,
        rows: clientesAltas.map((c) => ({
          id: c.id,
          nombre: c.nombre,
          email: c.email,
          fecha: c.fechaAlta,
          detalle: c.mes,
        })),
      };
    }

    if (kpiSeleccionado === 'bajas') {
      return {
        titulo: `Clientes de baja (${clientesBajas.length})`,
        rows: clientesBajas.map((c) => ({
          id: c.id,
          nombre: c.nombre,
          email: c.email,
          fecha: c.fechaBaja,
          detalle: MOTIVO_LABELS[c.motivo as keyof typeof MOTIVO_LABELS] || c.motivo || '-',
        })),
      };
    }

    if (kpiSeleccionado === 'altaAcademia') {
      return {
        titulo: 'Altas Academia',
        rows: data.meta?.kpis?.academia?.detalles?.altasPeriodo ?? [],
      };
    }
    if (kpiSeleccionado === 'bajaAcademiaNeta') {
      return {
        titulo: 'Bajas Academia (netas)',
        rows: data.meta?.kpis?.academia?.detalles?.bajasNetasPeriodo ?? [],
      };
    }
    if (kpiSeleccionado === 'altaProyecto') {
      return {
        titulo: 'Altas Proyecto',
        rows: data.meta?.kpis?.proyecto?.detalles?.altasPeriodo ?? [],
      };
    }
    if (kpiSeleccionado === 'bajaProyectoNeta') {
      return {
        titulo: 'Bajas Proyecto (netas)',
        rows: data.meta?.kpis?.proyecto?.detalles?.bajasNetasPeriodo ?? [],
      };
    }
    if (kpiSeleccionado === 'bajaSoloProyecto') {
      return {
        titulo: 'Bajas solo de Proyecto',
        rows: data.meta?.kpis?.proyecto?.detalles?.bajasSoloProyecto ?? [],
      };
    }
    if (kpiSeleccionado === 'transferenciasSalientes') {
      return {
        titulo: 'Transferencias salientes',
        rows: data.meta?.kpis?.proyecto?.detalles?.transferenciasSalientes ?? [],
      };
    }
    if (kpiSeleccionado === 'reactivacionesProyecto') {
      return {
        titulo: 'Reactivaciones Proyecto',
        rows: data.meta?.kpis?.proyecto?.detalles?.reactivacionesProyecto ?? [],
      };
    }

    return { titulo: '', rows: [] };
  };

  const detalleKpi = getDetalleKpi();

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h4">Altas vs Bajas de Clientes</Typography>
        <ReportHelpButton
          title="¿Cómo funciona este reporte?"
          intro="Este resumen mide entradas/salidas de alumnado según actividad académica y estado del usuario en el periodo filtrado."
          sections={[
            {
              title: 'Qué cuenta como alta',
              items: [
                'Alta = primera reserva válida del alumno dentro del periodo (no la fecha de creación del usuario).',
                'Se consideran solo reservas no canceladas y proyectos que cuentan en reportes de alumnos.',
                'Los filtros de Proyecto, Year/Curso, Centro y Formato definen el universo analizado.',
              ],
            },
            {
              title: 'Qué cuenta como baja y cómo se clasifica',
              items: [
                'Baja = desactivación registrada en el histórico de estados del usuario (usuarios_estado_log).',
                'Si desmarcas “Incluir bajas automáticas”, solo se contabiliza motivo BAJA (voluntaria).',
                'Si está marcado, también se incluyen bajas automáticas como NO_MAS_CLASE.',
                `Además se calculan KPIs netos de academia/proyecto con ventana de ${
                  data?.meta?.churnWindowDays ?? 60
                } días para separar bajas reales, transferencias y reactivaciones.`,
              ],
            },
            {
              title: 'Lectura de resultados',
              items: [
                'Saldo Neto = Altas - Bajas en el periodo visible.',
                'Puedes hacer clic en KPIs y en el gráfico de motivos para ver el detalle de alumnos.',
              ],
            },
          ]}
        />
      </Box>

      {/* Filtros de fecha */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Hasta"
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="contained" onClick={() => refetch()} disabled={isLoading} fullWidth>
                {isLoading ? <CircularProgress size={24} /> : 'Generar Reporte'}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={proyectos}
                value={proyectos.filter((p) => proyectoIds.includes(p.id))}
                onChange={(_, value) => setProyectoIds(value.map((p) => p.id))}
                getOptionLabel={(o) => o.nombre}
                renderInput={(params) => <TextField {...params} label="Proyectos" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={CURSOS as readonly string[]}
                value={grades}
                onChange={(_, value) => setGrades(value as string[])}
                renderInput={(params) => <TextField {...params} label="Year / Curso" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={centros}
                value={centros.filter((c) => centroIds.includes(c.id))}
                onChange={(_, value) => setCentroIds(value.map((c) => c.id))}
                getOptionLabel={(o) => o.nombre}
                renderInput={(params) => <TextField {...params} label="Centros" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={formatos}
                value={formatos.filter((f) => formatoIds.includes(f.id))}
                onChange={(_, value) => setFormatoIds(value.map((f) => f.id))}
                getOptionLabel={(o) => o.nombreFormato}
                renderInput={(params) => <TextField {...params} label="Formatos" />}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeAutoBajas}
                    onChange={(e) => setIncludeAutoBajas(e.target.checked)}
                  />
                }
                label="Incluir bajas automáticas (NO_MAS_CLASE)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {data?.meta && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Definición alta: primera reserva válida · Universo filtrado:{' '}
            {data.meta.universoAlumnos ?? 0} alumnos · Ventana churn:{' '}
            {data.meta.churnWindowDays ?? 60} días
          </Typography>
          <Alert severity={proyectoIds.length ? 'info' : 'warning'}>
            {proyectoIds.length
              ? 'KPIs de Proyecto calculados para los proyectos seleccionados en el filtro.'
              : 'No hay proyecto seleccionado: los KPIs de Proyecto se calculan sobre el segmento filtrado actual (no sobre un proyecto único).'}
          </Alert>
        </Box>
      )}

      {data?.meta?.kpis && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              KPIs netos (Academia)
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card onClick={() => setKpiSeleccionado('altaAcademia')} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Altas Academia
                </Typography>
                <Typography variant="h4" color="success.main">
                  {data.meta.kpis.academia?.altasPeriodo ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card onClick={() => setKpiSeleccionado('bajaAcademiaNeta')} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Bajas Academia (netas)
                </Typography>
                <Typography variant="h4" color="error.main">
                  {data.meta.kpis.academia?.bajasNetasPeriodo ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              KPIs netos (Proyecto)
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card onClick={() => setKpiSeleccionado('altaProyecto')} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Altas Proyecto
                </Typography>
                <Typography variant="h4" color="success.main">
                  {data.meta.kpis.proyecto?.altasPeriodo ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card onClick={() => setKpiSeleccionado('bajaProyectoNeta')} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Bajas Proyecto (netas)
                </Typography>
                <Typography variant="h4" color="error.main">
                  {data.meta.kpis.proyecto?.bajasNetasPeriodo ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card onClick={() => setKpiSeleccionado('bajaSoloProyecto')} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Bajas solo de Proyecto
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {data.meta.kpis.proyecto?.bajasSoloProyecto ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              onClick={() => setKpiSeleccionado('transferenciasSalientes')}
              sx={{ cursor: 'pointer' }}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Transferencias salientes
                </Typography>
                <Typography variant="h4" color="info.main">
                  {data.meta.kpis.proyecto?.transferenciasSalientes ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              onClick={() => setKpiSeleccionado('reactivacionesProyecto')}
              sx={{ cursor: 'pointer' }}
            >
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Reactivaciones Proyecto
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {data.meta.kpis.proyecto?.reactivacionesProyecto ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Indicadores totales */}
      {data && totales && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card onClick={() => setKpiSeleccionado('altas')} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Altas
                </Typography>
                <Typography variant="h3" color="success.main">
                  {totales.altas}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card onClick={() => setKpiSeleccionado('bajas')} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Bajas
                </Typography>
                <Typography variant="h3" color="error.main">
                  {totales.bajas}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card onClick={() => setKpiSeleccionado('saldo')} sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Saldo Neto
                </Typography>
                <Typography
                  variant="h3"
                  color={totales.altas - totales.bajas >= 0 ? 'success.main' : 'error.main'}
                >
                  {totales.altas - totales.bajas >= 0 ? '+' : ''}
                  {totales.altas - totales.bajas}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {data &&
        kpiSeleccionado &&
        (kpiSeleccionado === 'saldo' ? true : detalleKpi.rows.length >= 0) && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">
                  {kpiSeleccionado === 'altas' && `Clientes de alta (${clientesAltas.length})`}
                  {kpiSeleccionado === 'bajas' && `Clientes de baja (${clientesBajas.length})`}
                  {kpiSeleccionado === 'saldo' &&
                    `Detalle de altas y bajas (Altas: ${clientesAltas.length} · Bajas: ${clientesBajas.length})`}
                  {kpiSeleccionado !== 'saldo' &&
                    kpiSeleccionado !== 'altas' &&
                    kpiSeleccionado !== 'bajas' &&
                    `${detalleKpi.titulo} (${detalleKpi.rows.length})`}
                </Typography>
                <IconButton size="small" onClick={() => setKpiSeleccionado(null)}>
                  ✕
                </IconButton>
              </Box>

              {(kpiSeleccionado === 'altas' || kpiSeleccionado === 'saldo') && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Altas
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Fecha de Alta</TableCell>
                        <TableCell>Mes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientesAltas.map((cliente) => (
                        <TableRow key={`alta-${cliente.id}-${cliente.mes ?? ''}`}>
                          <TableCell>{cliente.id}</TableCell>
                          <TableCell>{cliente.nombre}</TableCell>
                          <TableCell>{cliente.email}</TableCell>
                          <TableCell>
                            {format(new Date(cliente.fechaAlta), 'dd/MM/yyyy HH:mm')}
                          </TableCell>
                          <TableCell>{cliente.mes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {(kpiSeleccionado === 'bajas' || kpiSeleccionado === 'saldo') && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Bajas
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Fecha de Baja</TableCell>
                        <TableCell>Motivo</TableCell>
                        <TableCell>Mes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientesBajas.map((cliente) => (
                        <TableRow key={`baja-${cliente.id}-${cliente.mes ?? ''}`}>
                          <TableCell>{cliente.id}</TableCell>
                          <TableCell>{cliente.nombre}</TableCell>
                          <TableCell>{cliente.email}</TableCell>
                          <TableCell>
                            {format(new Date(cliente.fechaBaja), 'dd/MM/yyyy HH:mm')}
                          </TableCell>
                          <TableCell>
                            {MOTIVO_LABELS[cliente.motivo as keyof typeof MOTIVO_LABELS] ||
                              cliente.motivo ||
                              '-'}
                          </TableCell>
                          <TableCell>{cliente.mes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {kpiSeleccionado !== 'saldo' &&
                kpiSeleccionado !== 'altas' &&
                kpiSeleccionado !== 'bajas' && (
                  <Box sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Detalle</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detalleKpi.rows.map((row) => (
                          <TableRow key={`${kpiSeleccionado}-${row.id}-${row.fecha ?? ''}`}>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.nombre}</TableCell>
                            <TableCell>{row.email}</TableCell>
                            <TableCell>
                              {row.fecha ? format(new Date(row.fecha), 'dd/MM/yyyy') : '-'}
                            </TableCell>
                            <TableCell>{row.detalle ?? '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
            </CardContent>
          </Card>
        )}

      {/* Gráfico de líneas: Altas vs Bajas */}
      {data && lineChartData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Evolución mensual
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={lineChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="mes"
                  tickFormatter={(value) =>
                    usarDiario ? format(parseISO(String(value)), 'dd/MM') : value
                  }
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Altas" stroke="#4caf50" strokeWidth={2} />
                <Line type="monotone" dataKey="Bajas" stroke="#ef5350" strokeWidth={2} />
                <Line
                  type="monotone"
                  dataKey="Saldo"
                  stroke="#2196f3"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Gráfico de pastel: Motivos de baja */}
      {data && pieChartData.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Motivos de baja (total) - Haz clic en una sección para ver detalles
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  onClick={handlePieClick}
                  cursor="pointer"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[entry.motivo as keyof typeof COLORS]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Tabla de clientes por motivo seleccionado */}
      {motivoSeleccionado && clientesSeleccionados.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Clientes dados de baja -{' '}
              {MOTIVO_LABELS[motivoSeleccionado as keyof typeof MOTIVO_LABELS] ||
                motivoSeleccionado}{' '}
              ({clientesSeleccionados.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Fecha de Baja</TableCell>
                  <TableCell>Desactivado por</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientesSeleccionados.map((cliente) => (
                  <TableRow key={cliente.id}>
                    <TableCell>{cliente.id}</TableCell>
                    <TableCell>{cliente.nombre}</TableCell>
                    <TableCell>{cliente.email}</TableCell>
                    <TableCell>{format(new Date(cliente.fechaBaja), 'dd/MM/yyyy HH:mm')}</TableCell>
                    <TableCell>{cliente.actorNombre}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Estado inicial */}
      {!data && !isLoading && (
        <Card>
          <CardContent>
            <Typography color="textSecondary" align="center">
              Selecciona un rango de fechas y haz clic en "Generar Reporte"
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
